/**
 * @file utils/directory-sync.ts
 * @description 獲證組織名錄「自動匯總」純函數（客戶 PPT S6）。
 *
 * 這些函數不觸 DB，供 API service（syncFromCertificate / syncFromAuditTask）、
 * Excel 導出與前端列表共用，並可獨立做單元測試：
 * - computeRotationWarning()   稽核組連任 3 年預警
 * - formatSalesChannels()      銷售通路代碼 → 中文標籤字串
 * - formatCounselor()          輔導員姓名/電話拼接
 * - deriveDirectoryArea()      由申請明細推導名錄面積（公頃）
 * - summarizeCompletedCases()  已完成驗證年度/類型彙總
 * - mapVerificationOptionToType() 驗證選項代碼 → 個別驗證/集團驗證
 * - formatProcessingSites()    採收後處理場所 jsonb → 顯示字串
 * - daysSinceDate()            距指定日期天數（列表/導出即時計算，不落庫）
 */
/** 產品銷售對象 / 通路代碼 → 中文標籤（與 cases.applicationDetails.salesChannels 對應）。 */
export const DIRECTORY_SALES_CHANNEL_LABELS = {
    auction: '拍賣市場',
    supermarket: '超市賣場',
    processor: '加工業者',
    distributor: '分裝/流通業者',
    other: '其他',
};
/**
 * 取稽核員快照欄位中的「姓名」部分。
 * 快照格式約定為「姓名-動作」（例：王淳妍-追二）；無 '-' 時整串視為姓名。
 */
export function extractAuditorName(slot) {
    if (!slot)
        return null;
    const name = slot.split('-')[0]?.trim();
    return name || null;
}
/**
 * 稽核組連任 3 年預警。
 *
 * 規則：以年度順序排列的稽核員快照（auditorY1~Y4，索引即年度先後），
 * 取「最近一個有值的年度」往前看——若最近 3 個**連續**年度皆有值、
 * 且姓名相同（忽略「-動作」後綴），視為連任 3 年 → true。
 *
 * @param slots 年度稽核員快照陣列（舊 → 新，如 [auditorY1, auditorY2, auditorY3, auditorY4]）
 */
export function computeRotationWarning(slots) {
    const names = slots.map(extractAuditorName);
    // 找最近一個有值的年度索引
    let last = -1;
    for (let i = names.length - 1; i >= 0; i--) {
        if (names[i]) {
            last = i;
            break;
        }
    }
    if (last < 2)
        return false; // 不足 3 個年度
    const [a, b, c] = [names[last - 2], names[last - 1], names[last]];
    return !!a && !!b && !!c && a === b && b === c;
}
/**
 * 銷售通路代碼陣列 → 中文標籤字串（頓號分隔）。
 * 未知代碼原樣輸出；空陣列/未提供回 null。
 */
export function formatSalesChannels(channels) {
    if (!channels?.length)
        return null;
    return channels.map((c) => DIRECTORY_SALES_CHANNEL_LABELS[c] ?? c).join('、');
}
/**
 * 輔導員快照字串：「姓名（電話）」；無電話只留姓名；無姓名回 null。
 */
export function formatCounselor(name, phone) {
    const n = name?.trim();
    if (!n)
        return null;
    const p = phone?.trim();
    return p ? `${n}（${p}）` : n;
}
/**
 * 由申請明細推導名錄面積（公頃）。
 * - 初次申請等填 applyAreaHa → 直接採用
 * - 追蹤/展延填 organicAreaHa + organicConversionAreaHa → 取合計
 * - 皆無 → null
 */
export function deriveDirectoryArea(details) {
    if (details.applyAreaHa != null)
        return details.applyAreaHa;
    const organic = details.organicAreaHa ?? 0;
    const conversion = details.organicConversionAreaHa ?? 0;
    if (details.organicAreaHa == null && details.organicConversionAreaHa == null)
        return null;
    return organic + conversion;
}
/**
 * 已完成驗證年度/類型彙總。
 * 年度取 caseCode 前 4 碼（格式 `{YYYY}-{TAP|CAS|ECO}NNNN-NN`）；去重、升冪。
 * 類型取 verificationType 去重（保持出現順序）。
 */
export function summarizeCompletedCases(rows) {
    const years = new Set();
    const types = [];
    for (const row of rows) {
        const y = row.caseCode?.slice(0, 4);
        if (y && /^\d{4}$/.test(y))
            years.add(y);
        const t = row.verificationType?.trim();
        if (t && !types.includes(t))
            types.push(t);
    }
    return {
        completedYears: years.size ? [...years].sort().join('、') : null,
        completedTypes: types.length ? types.join('、') : null,
    };
}
/**
 * 驗證選項代碼（cases.applicationDetails.verificationOption）→ 名錄驗證類別標籤。
 */
export function mapVerificationOptionToType(option) {
    if (option === 'individual')
        return '個別驗證';
    if (option === 'group')
        return '集團驗證';
    return null;
}
/**
 * 採收後處理場所陣列 → 顯示字串「名稱（地址）」頓號分隔；空回 null。
 * 容忍非陣列輸入（jsonb 欄位型別鬆散）。
 */
export function formatProcessingSites(sites) {
    if (!Array.isArray(sites) || sites.length === 0)
        return null;
    const parts = sites
        .map((s) => {
        const name = s?.name?.trim();
        const addr = s?.address?.trim();
        if (name && addr)
            return `${name}（${addr}）`;
        return name || addr || null;
    })
        .filter((s) => !!s);
    return parts.length ? parts.join('、') : null;
}
/**
 * 距指定日期（YYYY-MM-DD）至 now 的整數天數；未提供日期回 null；未來日期回 0。
 * 用於「距離上次發證天數」列表/導出即時計算（需求：不落庫）。
 */
export function daysSinceDate(dateStr, now = new Date()) {
    if (!dateStr)
        return null;
    const t = new Date(dateStr).getTime();
    if (Number.isNaN(t))
        return null;
    return Math.max(0, Math.floor((now.getTime() - t) / 86_400_000));
}
